import { useEffect } from 'react';

import {
  MarketsToCheck,
  SourcesToCheck,
  useNewsFiltersStore,
} from '~/stores/use-news-filters-store/useNewsFiltersStore';
import {
  useNewsWatchlistsStore,
  WatchlistToCheck,
} from '~/stores/use-news-watchlists/useNewsWatchlistsStore';
import { useWatchlist } from '~/stores/use-watchlist/useWatchlist';

import { ListType } from '../../SideAccordionFilters';
import { useGetFilters } from '../use-get-filters/useGetFilters';

type ListToCheckType = MarketsToCheck | WatchlistToCheck | SourcesToCheck;

export interface FilterStates {
  listToCheck: ListToCheckType[];
  isAllChecked: boolean;
  isPartialChecked: boolean;
  checkedCount: number;
}

const isAllCheckedFactory = (list: ListToCheckType[]) => {
  return list.length ? list.every(item => item.isChecked) : false;
};
const isPartialCheckedFactory = (
  isAllChecked: boolean,
  list: ListToCheckType[]
) => {
  return isAllChecked ? false : list.some(item => item.isChecked);
};
const checkedCountFactory = (list: ListToCheckType[]) => {
  return list.filter(item => item.isChecked).length;
};

export const useNewsFilters = () => {
  const { lists } = useWatchlist();
  const { data } = useGetFilters();

  const {
    watchlistsToCheck,
    initialize: watchlistInitialize,
    toggleSelectAll: watchlistToggleSelectAll,
    toggleSelectOne: watchlistToggleSelectOne,
  } = useNewsWatchlistsStore();
  const {
    marketsToCheck,
    sourcesToCheck,
    isFetchingMostRead,
    initialize,
    marketToggleSelectAll,
    marketToggleSelectOne,
    sourcesToggleSelectAll,
    sourcesToggleSelectOne,
    toggleIsFetchingMostRead,
    resetMostRead,
    resetDateRange,
  } = useNewsFiltersStore();

  const isAllWatchlistChecked = isAllCheckedFactory(watchlistsToCheck);
  const isAllMarketsChecked = isAllCheckedFactory(marketsToCheck);
  const isAllSourcesToCheck = isAllCheckedFactory(sourcesToCheck);

  useEffect(() => {
    watchlistInitialize(lists);
    if (data) {
      initialize({
        markets: data.markets,
        sources: data.sources,
      });
    }
  }, [data, initialize, lists, watchlistInitialize]);

  const handleToggleAll = (listType: ListType, prevIsChecked: boolean) => {
    switch (listType) {
      case ListType.Watchlists:
        watchlistToggleSelectAll(prevIsChecked);
        break;
      case ListType.Markets:
        marketToggleSelectAll(prevIsChecked);
        break;
      case ListType.Sources:
        sourcesToggleSelectAll(prevIsChecked);
        break;
    }
  };

  const handleToggleOne = (listType: ListType, itemKey: string) => {
    switch (listType) {
      case ListType.Watchlists:
        watchlistToggleSelectOne(itemKey);
        break;
      case ListType.Markets:
        marketToggleSelectOne(itemKey);
        break;
      case ListType.Sources:
        sourcesToggleSelectOne(itemKey);
        break;
    }
  };

  const toggleResetChecked = () => {
    watchlistToggleSelectAll(true);
    marketToggleSelectAll(true);
    sourcesToggleSelectAll(true);
    resetMostRead();
    resetDateRange();
  };

  const watchlistStates = {
    listToCheck: watchlistsToCheck,
    isAllChecked: isAllWatchlistChecked,
    isPartialChecked: isPartialCheckedFactory(
      isAllWatchlistChecked,
      watchlistsToCheck
    ),
    checkedCount: checkedCountFactory(watchlistsToCheck),
  };
  const marketsStates = {
    listToCheck: marketsToCheck,
    isAllChecked: isAllMarketsChecked,
    isPartialChecked: isPartialCheckedFactory(
      isAllMarketsChecked,
      marketsToCheck
    ),
    checkedCount: checkedCountFactory(marketsToCheck),
  };
  const sourcesStates = {
    listToCheck: sourcesToCheck,
    isAllChecked: isAllSourcesToCheck,
    isPartialChecked: isPartialCheckedFactory(
      isAllSourcesToCheck,
      sourcesToCheck
    ),
    checkedCount: checkedCountFactory(sourcesToCheck),
  };

  return {
    watchlistStates,
    marketsStates,
    sourcesStates,
    isFetchingMostRead,
    handleToggleAll,
    handleToggleOne,
    toggleResetChecked,
    toggleIsFetchingMostRead,
  };
};
